import PropTypes from "prop-types";
import * as Yup from "yup";
import {openSuccessDialog} from "../../utils/ui-components/pop-ups/SuccessDialog";
import {Form, Formik} from "formik";
import {Button, CircularProgress, Grid, IconButton, InputAdornment} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import {gridSpacing} from "../../store/constants";
import TextField from "../../utils/ui-components/FormsUI/TextField";
import Switch from "../../utils/ui-components/FormsUI/Switch";
import React, {useState} from "react";
import {ReportUserListValues} from "./report-user-list";
import ChangeReportUserPassword from "./change-report-user-password";
import { UserService } from "../../assets/_services/user-service";

ReportUserDetails.propTypes = {
    initialItem: PropTypes.object.isRequired,
    setOpen: PropTypes.func.isRequired,
    fetchData: PropTypes.func,
    initialData: PropTypes.object
};

function ReportUserDetails({initialItem, initialData, setOpen, fetchData}: any) {

    const [changePassord, setChangePassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [passwordShow, setPasswordShow] = useState({
        password: "",
        showPassword: false,
        confirmPassword: "",
        showConfirmPassword: false,
    });

    const FORM_VALIDATION_CREATION = Yup.object().shape({
        username: Yup.string()
        .max(255, 'Username cannot be more than 255 characters')
        .matches(/^[a-zA-Z0-9]*$/, 'Username can only contain letters and numbers')
        .required('Username is required'),
        password: Yup.string().max(255).required('Password is required').matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
            "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
        ),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], "Your passwords do not match.").required("Please retype your password."),
        status: Yup.boolean().required(),
        email: Yup.string().email('Invalid email address').notRequired(),
        mobileNumber : Yup.string().matches(
            /^((880|0)?(1){1}[3456789]{1}(\d){8},?)+$/,'Invalid mobile numbers pattern detected')
            .required("Please enter contact number"),
    });
    
    const FORM_VALIDATION_UPDATE = Yup.object().shape({
        username: Yup.string()
        .max(255, 'Username cannot be more than 255 characters')
        .matches(/^[a-zA-Z0-9]*$/, 'Username can only contain letters and numbers')
        .required('Username is required'),
        status: Yup.boolean().required(),
        email: Yup.string().email('Invalid email address').required('Email is required')
    });

    const createReportUser = (itemData: ReportUserListValues, setSubmitting: (isSubmitting: boolean) => void) => {
        setIsLoading(true)
        let formData = {
            userName: itemData.username,
            password: itemData.password,
            status: itemData.status ? 1 : 0,
            email: itemData.email,
            mobile: itemData?.mobileNumber
        }
        UserService.createReportUser(formData).then(
            response => {
                if (response.isSuccess) {
                    openSuccessDialog("Success", response.data.comment);
                    setOpen(false);
                    fetchData("");
                    setIsLoading(false)
                } else {
                    setSubmitting(false);
                    setIsLoading(false)
                }
            }
        );
    };

    const updateReportUser = (itemData: ReportUserListValues, setSubmitting: (isSubmitting: boolean) => void) => {
        setIsLoading(true)
        let formData = {
            userId: itemData.reportUserId,
            userName: itemData.username,
            status: itemData.status ? 1 : 0,
            email: itemData.email,
            mobile: itemData?.mobileNumber
        }
        UserService.updateReportUserDetails(formData).then(
            response => {
                setSubmitting(false);
                if (response.isSuccess) {
                    openSuccessDialog("Success", response.data.comment);
                    setOpen(false);
                    fetchData(initialData.search);
                    setIsLoading(false)
                } else {
                    setSubmitting(false);
                    setIsLoading(false)
                }
            }
        );
    };

    const handleClickShowPassword = () => {
        setPasswordShow({
            ...passwordShow,
            showPassword: !passwordShow.showPassword,
        });
    };

    const handleClickShowConfirmPassword = () => {
        setPasswordShow({
            ...passwordShow,
            showConfirmPassword: !passwordShow.showConfirmPassword,
        });
    };

    return (
        <>
            {!changePassord ?
                (<Formik
                    initialValues={{
                        ...initialItem
                    }}
                    validationSchema={initialItem.reportUserId ? FORM_VALIDATION_UPDATE : FORM_VALIDATION_CREATION}
                    onSubmit={(values: ReportUserListValues, {setSubmitting}) => {
                        if (initialItem.reportUserId) {
                            updateReportUser(values, setSubmitting);
                        } else {
                            createReportUser(values, setSubmitting);
                        }
                    }}
                >
                    {({values, dirty, isSubmitting, isValid, setFieldValue}) => (
                        <Form>
                            <Grid container columnSpacing={gridSpacing}>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <TextField
                                        required
                                        type={"text"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonIcon/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        name="username"
                                        label="Username"
                                        placeholder="Username"
                                        disabled={initialItem?.userId ? true : false}
                                        />
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <TextField
                                        required
                                        label="Mobile"
                                        name="mobileNumber"
                                        type="tel"
                                        InputProps={{
                                            startAdornment: <InputAdornment
                                                position="start">+880</InputAdornment>,
                                        }}/>
                                </Grid>
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <TextField
                                        required
                                        type={"email"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonIcon/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        name="email"
                                        label="Email Address"
                                        placeholder="abc@gmail.com"
                                        disabled={initialItem?.userId ? true : false}
                                    />
                                </Grid>
                                {!initialItem?.reportUserId &&
                                    <>
                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                            <TextField
                                                type={passwordShow.showPassword ? "text" : "password"}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <LockIcon/>
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickShowPassword}
                                                                edge="end"
                                                            >
                                                                {passwordShow.showPassword ? (
                                                                    <VisibilityOff/>
                                                                ) : (
                                                                    <Visibility/>
                                                                )}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                name="password"
                                                label="Password"
                                                placeholder="Password"
                                                required
                                            />
                                        </Grid>
                                        <Grid item lg={6} md={6} sm={12} xs={12}>
                                            <TextField
                                                type={passwordShow.showConfirmPassword ? "text" : "password"}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <LockIcon/>
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickShowConfirmPassword}
                                                                edge="end"
                                                            >
                                                                {passwordShow.showConfirmPassword ? (
                                                                    <VisibilityOff/>
                                                                ) : (
                                                                    <Visibility/>
                                                                )}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                name="confirmPassword"
                                                label="Confirm Password"
                                                placeholder="Confirm Password"
                                                required
                                            />
                                        </Grid>
                                    </>
                                }
                                <Grid item lg={2} md={2} sm={4} xs={4}>
                                    <Switch
                                        name="status"
                                        label={values.status ? "ACTIVE" : "INACTIVE"}
                                        style={{co: 'green'}}
                                    />
                                </Grid>
                                <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <Grid item xs={12} sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        py: 2,
                                    }}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            type="submit"
                                            disabled={!dirty || isSubmitting || !isValid}
                                        >
                                            {isLoading && <CircularProgress size={'20px'} sx={{
                                                mr: 1,
                                                color: 'gray'
                                            }}/>}{initialItem.reportUserId ? 'Save' : 'Add'} Report User
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>) : (
                    <ChangeReportUserPassword id={initialItem.reportUserId} setOpen={setOpen} fetchData={fetchData} search={initialData.search}/>
                )
            }

            {initialItem?.reportUserId &&
                (<Grid container justifyContent={'flex-start'}>
                    <Grid item xs={3}>
                        {!changePassord ?
                            (
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setChangePassword(true)
                                    }}
                                >Change Password</Button>
                            ) : (
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setChangePassword(false)
                                    }}
                                >Update Report User</Button>
                            )}
                    </Grid>
                </Grid>)
            }

        </>
    );
}

export default ReportUserDetails;